import { LockOutlined } from '@ant-design/icons';
import {
  AccordionItem,
  Alert,
  Banner,
  Checkbox,
  Heading,
  Option,
  RadioInput,
  Select,
  Spinner,
  Tooltip,
} from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { GoogleSheetsIcon } from '@mm/icons';
import { XRaySlideoutBanner } from '@mm/icons/SlideoutBanners';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { CampaignsOverviewPayload } from '@mm/state/model/CampaignOverviewModel';
import {
  PromoterModel,
  UsedInCampaignType,
  XRaySettingsType,
} from '@mm/state/model/PromoterModel';

import { selectGoogleSheetsConnectionsByAccountId } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { chatWidgetTroubleshootingLastResultSelector } from '@mm/state/selectors/chatWidgetTroubleshootingSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  combinedFeatureFlagsSelector,
  companyFeatureFlagsSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { ChatWidgetTroubleshootingTool } from '@mm/stateful-components';
import { CustomerChatConditionRulesEditor } from '@mm/stateful-components/CustomerChat/CustomerChatConditionRulesEditor/CustomerChatConditionRulesEditor';
import { useCustomerChatConditionsEditorPromoterState } from '@mm/stateful-components/CustomerChat/use-customer-chat-conditions-editor-state';
import { Button, InputNumber } from 'antd';
import classNames from 'classnames';
import { first } from 'lodash';
import pluralize from 'pluralize';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import PrimarySelect, {
  PrimarySelectOuterProps,
} from '../../../../../common/components/PrimarySelect/PrimarySelect';
import TextInput from '../../../../../common/components/TextInput';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import automationStyles from '../../../../features/beta/modules/Automations/automationStyles.module.css';
import { useAutomations } from '../../../../features/beta/modules/Automations/use-automations';
import { CustomerChatInstallInformation } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatInstallInformation/CustomerChatInstallInformation';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { GetemailsSetupSlideout } from '../GetemailsSetupSlideout/GetemailsSetupSlideout';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './GetemailsSlideoutEditorNew.module.css';

const XraySelectOptions: Option<
  NonNullable<PromoterModel['xrayMinActivityType']>
>[] = [
  { id: 'clicks', label: 'Clicked' },
  { id: 'views', label: 'Viewed' },
];
export const AutomationSelector = ({
  ...props
}: Omit<PrimarySelectOuterProps, 'options' | 'value'> & {
  value?: UsedInCampaignType;
}) => {
  const [automations, setAutomations] = useState<CampaignsOverviewPayload>();

  const actions = useAutomations();

  const fetchMore = async () => {
    if (automations?.pagination && automations?.campaigns) {
      const res = await actions.fetchAutomations({
        page: automations.pagination.page + 1,
        perPage: 50,
      });

      if (res) {
        setAutomations({
          campaigns: [...automations.campaigns, ...res.campaigns],
          pagination: res.pagination,
        });
      }
    }
  };

  useEffect(() => {
    (async () => {
      const res = await actions.fetchAutomations({ perPage: 50 });
      if (res) {
        setAutomations(res);
      }
    })();
  }, []);

  return (
    <PrimarySelect
      renderWithoutPopover
      className={classNames(
        automationStyles.select,
        styles.select,
        props?.className,
      )}
      useAntIcons
      customOptionRenderer={opt => <>{opt.name}</>}
      options={automations?.campaigns || []}
      value={props.value}
      {...props}
    />
  );
};

export type GetemailsSlideoutEditorNewProps = {
  onUnmount: () => {};
  promoterId: number;
};
export const GetemailsSlideoutEditorNewSettings = ({
  promoterId,
}: GetemailsSlideoutEditorNewProps) => {
  const hasXRaySubscriptionFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).xray;
  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const promoters = useSelector(state =>
    selectPromotersByType(state, 'getemails'),
  );
  const hasVoyagerEnrichmentFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).voyagerEnrichment;

  const hasVoyagerCompanyFlag = useSelector(
    companyFeatureFlagsSelector,
  ).voyager;

  const canEditConditions = useSelector(
    combinedFeatureFlagsSelector,
  ).customerChatEditConditions;
  const promoter = promoters.find(p => p.id === promoterId);
  const promoterCRUDActions = usePromoterCRUDActions();
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const connectionCRUDActions = useConnectionCRUDActions();

  const isPromoterCreatedLessThan10MinutesAgo = promoter?.createdAt
    ? Date.now() - new Date(promoter.createdAt).getTime() < 600000
    : false;

  const { leadType } = promoter?.settings?.xray || {};

  const { setRef } = useSlideoutOnClose();
  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const {
    localPromoter,
    setLocalPromoter,
    updatePromoter,
    updateLocalPromoterField,
  } = useCustomerChatConditionsEditorPromoterState(promoter);

  const googleSheetWebhookOptions = useSelector(
    selectGoogleSheetsConnectionsByAccountId,
  )
    .map<Option<number>[]>(a =>
      a.connections.map(connection => ({
        id: connection.id,
        label: connection.name,
      })),
    )
    .flat();

  const webhookOptions = [
    {
      id: -1,
      label: (
        <div className={styles.selectItemHeading}>
          <GoogleSheetsIcon color="#21A465" size={20} />
          Google Sheets
        </div>
      ),
      heading: true,
    },
    ...googleSheetWebhookOptions,
  ];

  const selectedWebhookOptions = webhookOptions.filter(o =>
    (promoter?.connectionWebhookIds || []).includes(o.id),
  );

  const promoterHasNoConditions = (promoter: PromoterModel) =>
    !promoter.conditions?.rules.length ||
    promoter.conditions.rules.every(r => r.operator === '*');

  const warnAboutMultipleRuleFreePromoters =
    promoter &&
    promoterHasNoConditions(promoter) &&
    promoters.some(p => p.id !== promoter.id && promoterHasNoConditions(p));

  const duplicateRules =
    !warnAboutMultipleRuleFreePromoters && promoter?.conditions?.rules.length
      ? promoter.conditions.rules.filter(rule =>
          promoters.some(
            p =>
              p.id !== promoter.id &&
              (p.conditions?.rules || []).some(
                otherRule =>
                  otherRule.operator === rule.operator &&
                  otherRule.value === rule.value,
              ),
          ),
        )
      : [];

  const onPromoterChange = async (
    key: string,
    value: any,
    onCatch?: () => void,
  ) => {
    try {
      await promoterCRUDActions.update(chatbotId, {
        id: promoter?.id,
        [key]: value,
      });
      successToast('Your changes have been saved.', 'top-left');
    } catch (e) {
      onCatch?.();
      errorToast('Something went wrong', 'top-left');
    }
  };

  const onLeadsTypeUpdate = async (leadType: XRaySettingsType) => {
    if (!hasVoyagerEnrichmentFeature) {
      return;
    }
    const updatedSettings = {
      settings: {
        xray: {
          leadType,
        },
      },
    };
    try {
      await promoterCRUDActions.update(chatbotId, {
        id: promoter?.id,
        ...updatedSettings,
      });
      successToast('Your changes have been saved.', 'top-left');
    } catch (e) {
      errorToast('Something went wrong', 'top-left');
    }
  };

  const customActivateAction = async () => {
    if (!hasXRaySubscriptionFlag) {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['STARTBOTS-X_RAY_ACTIVATION-EDITOR'],
        feature: 'xray',
        onSuccessBilling: () => onPromoterChange('active', true),
      });
    }

    if (hasXRaySubscriptionFlag && !promoter?.active) {
      onPromoterChange('active', true);
    } else {
      onPromoterChange('active', false);
    }
  };

  async function fetchItem() {
    await promoterCRUDActions.retrievePromoterById(chatbotId, promoterId);
  }

  async function fetchItems() {
    await promoterCRUDActions.retrieveAll(chatbotId, 'getemails');
  }

  useEffect(() => {
    fetchItem();
    connectionCRUDActions.retrieveAll(facebookPageId, { type: 'all' });

    return () => {
      fetchItems();
    };
  }, [chatbotId, promoterCRUDActions]);

  const [stateName, setStateName] = useState(promoter?.name || '');

  const advancedOptionsDisabled = !useSelector(combinedFeatureFlagsSelector)
    .xrayAdvancedCaptureSettings;

  const onAdvancedReturningOnlyToggle = async () => {
    onPromoterChange(
      'xrayReturningVisitorOnly',
      !promoter?.xrayReturningVisitorOnly,
    );
  };

  const [advancedMinActivityCount, setAdvancedMinActivityCount] = useState(
    promoter?.xrayMinActivityCount ?? 1,
  );
  const onAdvancedMinActivitySave = async (
    values: Pick<
      PromoterModel,
      'xrayMinActivityEnabled' | 'xrayMinActivityType' | 'xrayMinActivityCount'
    >,
  ) => {
    const valuesToApply = {
      xrayMinActivityEnabled: promoter?.xrayMinActivityEnabled,
      xrayMinActivityCount: promoter?.xrayMinActivityCount ?? 1,
      xrayMinActivityType: promoter?.xrayMinActivityType ?? 'clicks',
      ...values,
    };
    try {
      await promoterCRUDActions.update(chatbotId, {
        id: promoter?.id,
        ...valuesToApply,
      });
      successToast('Your changes have been saved.', 'top-left');
    } catch (e) {
      errorToast('Something went wrong', 'top-left');
    }
  };

  const [advancedDelayValue, setAdvancedDelayValue] = useState(
    promoter?.xrayDelaySeconds ?? 30,
  );

  const onAdvancedDelayValueChange = async (value: number) => {
    setAdvancedDelayValue(value);
  };

  const onAdvancedDelayBlur = async () => {
    onPromoterChange('xrayDelaySeconds', advancedDelayValue, () =>
      setAdvancedDelayValue(promoter?.xrayDelaySeconds || 0),
    );
  };

  const onAdvancedDelayCaptureToggle = async () => {
    onPromoterChange('xrayDelayEnabled', !promoter?.xrayDelayEnabled);
  };

  const onNameInputBlur = async () => {
    const newName = stateName.trim();

    if (newName && newName !== promoter?.name) {
      onPromoterChange('name', newName);
    }
  };

  const audienceID = promoter?.audiences?.[0]?.id;
  const defaultAudienceName =
    promoter?.audiences?.find(v => v.id === audienceID)?.name || '';

  const [audienceName, setAudienceName] = useState(defaultAudienceName || '');

  const [fetchedAudience, setFetchedAudience] = useState<
    AudiencesModel | undefined
  >();

  const isAudienceCreatedLessThan10MinutesAgo = fetchedAudience?.createdAt
    ? Date.now() - new Date(fetchedAudience.createdAt).getTime() < 600000
    : false;
  useEffect(() => {
    setAudienceName(
      promoter?.audiences?.[0]?.name || defaultAudienceName || '',
    );
  }, [promoter?.audiences?.[0]?.name]);

  const fetchAudience = async () => {
    if (audienceID && chatbotId) {
      const audience = await audiencesCRUDActions.retrieveAudienceById(
        chatbotId,
        audienceID,
      );
      setFetchedAudience(audience);
    }
  };

  // our api does not allow partial audience updates, so fetching to preserve filters
  useEffect(() => {
    if (audienceID) {
      fetchAudience();
    }
  }, [audienceID]);

  const onAudienceInputBlur = async () => {
    const newName = audienceName.trim();

    const audience = promoter?.audiences?.find(v => v.id === audienceID);

    if (newName && audience && newName !== audience?.name) {
      try {
        await audiencesCRUDActions.update(
          {
            ...audience,
            ...(fetchedAudience || {}),
            name: newName,
          },
          chatbotId,
        );
        await fetchItem();

        successToast('Your changes have been saved.', 'top-left');
      } catch (e) {
        errorToast('Something went wrong', 'top-left');
      }
    }
  };

  const onWebhookSelect = async selectedWebhookOptions => {
    try {
      await promoterCRUDActions.update(chatbotId, {
        id: promoter?.id,
        connectionWebhookIds: selectedWebhookOptions.map(({ id }) => id),
      });
      successToast('Your changes have been saved.', 'top-left');
    } catch (e) {
      errorToast('Something went wrong', 'top-left');
    }
  };

  const onAutomationSelect = async campaign => {
    if (promoter?.usedInCampaigns?.some(c => c.id === campaign.id)) {
      return;
    }

    try {
      await promoterCRUDActions.moveToCampaign({
        botId: chatbotId,
        campaignId: campaign.id,
        promoterId,
      });
      successToast('Your changes have been saved.', 'top-left');
    } catch (e) {
      errorToast('Something went wrong', 'top-left');
    }
  };

  const promoterHasNoNameOrDefaultName =
    !stateName.trim() ||
    (stateName.includes('Untitled Website Visitor Email Detector') &&
      isPromoterCreatedLessThan10MinutesAgo);

  const audienceHasNoNameOrDefaultName =
    !audienceName.trim() ||
    (audienceName.includes('Untitled Website Visitor Email Detector') &&
      isAudienceCreatedLessThan10MinutesAgo);

  if (!promoter) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader
        className={styles.header}
        promoter={promoter}
        title="X-Ray Lead Generation Pixel"
        customActivateAction={customActivateAction}
        deleteWithPromoterCRUDAction
      />
      <SlideoutMenu.Content className={styles.content}>
        <Banner
          css={{
            container: styles.bannerContainer,
            header: styles.bannerHeader,
            collapsedHeader: styles.collapsedHeader,
            content: styles.bannerContent,
          }}
          withHeaderArrowIcon
          showCloseIcon={false}
          withBannerHeader
          headerText="How does X-Ray Pixel Work?"
          withLeftArea={false}
          expandedOnMount={false}
          usedInSlideout
        >
          <XRaySlideoutBanner
            textColor="white"
            alt="How does X-Ray Pixel Work?"
          />
        </Banner>

        <CollapsibleContentContainer
          orderNumber="1"
          containerName="Specify what pages on your Website should trigger this automation"
          className={styles.collapsibleContainer}
          childrenContainerClassName={styles.collapsibleContainerDropdown}
          stepperStylingPlatform="xray"
          hideTitleBorder
          useCircleNumberStyle
        >
          <div className={styles.boxContent}>
            <div className={styles.box}>
              <div className={styles.boxBlock}>
                <Heading
                  text="X-Ray Pixel Trigger Name"
                  isBold
                  size={5}
                  className={styles.labelHeading}
                />
                <TextInput
                  className={styles.nameInput}
                  value={stateName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setStateName(e.target.value)
                  }
                  placeholder="X-Ray Pixel trigger name"
                  onBlur={() => onNameInputBlur()}
                  warning={promoterHasNoNameOrDefaultName}
                />
                {promoterHasNoNameOrDefaultName && (
                  <Alert
                    type="warning"
                    showCloseIcon={false}
                    className={styles.warning}
                  >
                    Please name new X-Ray Trigger to continue
                  </Alert>
                )}
              </div>

              <div className={styles.boxBlock} style={{ gap: '16px' }}>
                <CustomerChatConditionRulesEditor
                  usedInSlideout
                  updateLocalPromoterField={updateLocalPromoterField}
                  updatePromoter={updatePromoter}
                  localPromoter={localPromoter}
                  setLocalPromoter={setLocalPromoter}
                  canEditConditions={canEditConditions}
                  ruleTitle={
                    <Heading size={5} isBold className={styles.boxHeading}>
                      Capture leads when website visitors meet the following
                      conditions:
                    </Heading>
                  }
                  urlTitle={
                    <Heading size={5} className={styles.boxHeading}>
                      URL that the user is visiting:
                    </Heading>
                  }
                  warnAboutMultipleRuleFreePromoters={
                    warnAboutMultipleRuleFreePromoters
                  }
                  duplicateRules={duplicateRules}
                />
              </div>
            </div>
            <div className={styles.box}>
              <Heading
                text="What do you want to do with your leads?"
                isBold
                size={4}
                className={styles.boxHeading}
              />
              <div className={styles.boxBlock}>
                <Heading
                  text="Save to audience"
                  isBold
                  size={5}
                  className={styles.boxHeading}
                />

                <TextInput
                  className={styles.nameInput}
                  value={audienceName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAudienceName(e.target.value)
                  }
                  placeholder="Name your audience"
                  disabled={!fetchedAudience}
                  onBlur={() => onAudienceInputBlur()}
                  warning={audienceHasNoNameOrDefaultName}
                />

                {audienceHasNoNameOrDefaultName && (
                  <Alert
                    type="warning"
                    showCloseIcon={false}
                    className={styles.warning}
                  >
                    Please name new Audience to continue
                  </Alert>
                )}
              </div>
              <div className={styles.boxBlock}>
                <span className={styles.optional}>
                  <Heading text="Add to Automation" isBold size={5} />
                  <span>(optional)</span>
                </span>

                <AutomationSelector
                  customOptionsPosition="bottom"
                  renderWithoutPopover={false}
                  value={first(
                    promoter?.usedInCampaigns?.filter(
                      v => v.scheduleType === 'automation',
                    ),
                  )}
                  onChange={onAutomationSelect}
                />
              </div>
              <div className={styles.boxBlock}>
                <span className={styles.optional}>
                  <Heading text="Export leads to CRM" isBold size={5} />
                  <span>(optional)</span>
                </span>

                <Select
                  options={webhookOptions}
                  selected={selectedWebhookOptions}
                  onChange={onWebhookSelect}
                  className={styles.select}
                  hideClearButton
                />
              </div>
            </div>
            <AccordionItem
              className={classNames(styles.box, styles.advancedSettings)}
              childrenWrapperClassName={styles.advancedSettingsWrapper}
              closeOnOutsideClick={false}
              caretStyleIcon
              title={
                <div className={styles.featureHeader}>
                  <Heading
                    text="Advanced X-Ray Pixel Capture Settings"
                    isBold
                    size={4}
                  />
                  {advancedOptionsDisabled && (
                    <Button
                      type="primary"
                      size="small"
                      ghost
                      className={styles.secondaryButton}
                      icon={<LockOutlined />}
                      onClick={() => {
                        openUniversalUpgradeLandmineModal({
                          landmineName: landmineNames.XRAY_ADVANCED_SETTINGS,
                          onSuccessBilling: () =>
                            onPromoterChange('active', true),
                        });
                      }}
                    >
                      Unlock
                    </Button>
                  )}
                </div>
              }
            >
              {hasVoyagerEnrichmentFeature && hasVoyagerCompanyFlag && (
                <div
                  className={classNames(styles.feature, {
                    [styles.disabled]: advancedOptionsDisabled,
                  })}
                  style={{ gap: '4px' }}
                >
                  <Heading size={5} isBold>
                    Lead Type
                  </Heading>
                  <RadioInput
                    label="Personal emails"
                    name="consumer"
                    value="consumer"
                    disabled={advancedOptionsDisabled}
                    onChange={() => onLeadsTypeUpdate('consumer')}
                    checked={!leadType || leadType === 'consumer'}
                  />
                  <Tooltip
                    anchorElement={
                      <RadioInput
                        label="Business emails"
                        name="business"
                        value="business"
                        disabled={advancedOptionsDisabled}
                        onChange={() => onLeadsTypeUpdate('business')}
                        checked={leadType === 'business'}
                      />
                    }
                    css={{
                      content: styles.tooltip,
                      title: styles.tooltipContent,
                    }}
                    title="Uprade your plan to use this feature"
                    position="right"
                    toolTipDisabled={hasVoyagerEnrichmentFeature}
                    withoutLine
                    noDelayClose
                    useHover
                  />
                </div>
              )}
              <div
                className={classNames(styles.feature, {
                  [styles.disabled]: advancedOptionsDisabled,
                })}
              >
                <Heading size={5} isBold>
                  Audience behavior
                </Heading>

                <Checkbox
                  labelClassName={styles.checkboxLabel}
                  isChecked={!!promoter.xrayReturningVisitorOnly}
                  onClick={onAdvancedReturningOnlyToggle}
                  label="Only returning visitors (on second visit)"
                />

                <div className={styles.minActivityContainer}>
                  <Checkbox
                    labelClassName={styles.checkboxLabel}
                    isChecked={!!promoter.xrayMinActivityEnabled}
                    onToggle={e =>
                      onAdvancedMinActivitySave({
                        xrayMinActivityEnabled:
                          !promoter.xrayMinActivityEnabled,
                      })
                    }
                    label=""
                  />
                  Only users who
                  <Select
                    hideClearButton
                    hideCount
                    singleSelect
                    className={styles.minActivityTypeSelect}
                    options={XraySelectOptions}
                    selected={[
                      XraySelectOptions.find(
                        v => v.id === promoter.xrayMinActivityType,
                      ) || XraySelectOptions[0],
                    ]}
                    onChange={v =>
                      onAdvancedMinActivitySave({
                        xrayMinActivityType: v[0].id,
                      })
                    }
                    disabled={!promoter.xrayMinActivityEnabled}
                  />
                  <InputNumber
                    className={styles.minActivityCountInput}
                    min={1}
                    onChange={v => v && setAdvancedMinActivityCount(v)}
                    onBlur={e =>
                      onAdvancedMinActivitySave({
                        xrayMinActivityCount: advancedMinActivityCount,
                      })
                    }
                    value={advancedMinActivityCount}
                  />
                  {pluralize(
                    promoter.xrayMinActivityType === 'views' ? 'page' : 'time',
                    advancedMinActivityCount,
                  )}
                </div>

                <Checkbox
                  labelClassName={styles.checkboxLabel}
                  isChecked={!!promoter.xrayDelayEnabled}
                  onClick={onAdvancedDelayCaptureToggle}
                  label="Delay Capture"
                />

                <div
                  className={classNames(styles.timeInput, {
                    [styles.disabled]:
                      !promoter?.xrayDelayEnabled && !advancedOptionsDisabled,
                  })}
                >
                  Time that the user is active on site before capture:
                  <InputNumber
                    className={styles.numberInput}
                    min={1}
                    onChange={onAdvancedDelayValueChange}
                    onBlur={onAdvancedDelayBlur}
                    value={advancedDelayValue}
                    addonAfter="sec"
                  />
                </div>
              </div>
            </AccordionItem>
          </div>
        </CollapsibleContentContainer>

        <CollapsibleContentContainer
          orderNumber="2"
          containerName="Install Website Script"
          className={styles.collapsibleContainer}
          childrenContainerClassName={styles.collapsibleContainerDropdown}
          stepperStylingPlatform="xray"
          hideTitleBorder
          useCircleNumberStyle
          expandedOnMount={false}
        >
          <CustomerChatInstallInformation
            promoter={promoter}
            usedInSlideout
            hideWhitelist
          />
        </CollapsibleContentContainer>
        <CollapsibleContentContainer
          orderNumber="3"
          containerName="Verify the script is working properly"
          className={styles.collapsibleContainer}
          childrenContainerClassName={styles.collapsibleContainerDropdown}
          stepperStylingPlatform="xray"
          hideTitleBorder
          useCircleNumberStyle
          expandedOnMount={false}
        >
          <div className={styles.box}>
            <ChatWidgetTroubleshootingTool
              css={{
                container: styles.troubleshootContainer,
                textInput: styles.troubleshootInput,
                button: styles.troubleshootButton,
              }}
              promoter={promoter}
              buttonName="Test it"
              usedInSlideout
              isXRay
            />
          </div>
        </CollapsibleContentContainer>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};

type SlideoutState =
  | 'loading' // Need to fetch FB page from BE to check onceSuccessfullyLoadedGlobalJs
  | 'setup' // Show Global JS setup slideout
  | 'settings'; // Show Xray slideout editor

export const GetemailsSlideoutEditorNew = props => {
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const result = useSelector(chatWidgetTroubleshootingLastResultSelector);

  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const [slideoutState, setSlideoutState] = useState<SlideoutState>(
    (() => {
      // If we already know that we've loaded successfully, then we can skip straight to settings
      if (
        activeFacebookPage?.onceSuccessfullyLoadedGlobalJs ||
        result?.onceSuccessfullyLoadedGlobalJs
      ) {
        return 'settings';
      }

      // Loading only happens if we need to fetch a fresh copy of the FB page from the BE to check latest attrs
      return 'loading';
    })(),
  );

  // Refresh the FB page from the BE for the latest onceSuccessfullyLoadedGlobalJs value
  // Runs if either: 1. we just opened the slideout (in "loading" state), or 2. if we just finished a successful test
  useEffect(() => {
    if (
      (slideoutState === 'loading' ||
        (result?.onceSuccessfullyLoadedGlobalJs &&
          !activeFacebookPage?.onceSuccessfullyLoadedGlobalJs)) &&
      activeFacebookPage?.id
    ) {
      (async () => {
        try {
          const page = await connectedPageCRUDActions.retrievePage(
            activeFacebookPage?.id,
          );

          setSlideoutState(prevState =>
            prevState === 'loading'
              ? page?.onceSuccessfullyLoadedGlobalJs
                ? 'settings'
                : 'setup'
              : prevState,
          );
        } catch (e) {
          setSlideoutState(prevState =>
            prevState === 'loading' ? 'settings' : prevState,
          );
        }
      })();
    }
  }, [result?.onceSuccessfullyLoadedGlobalJs]);

  switch (slideoutState) {
    case 'loading':
      return <Spinner standalone />;
    case 'setup':
      return <GetemailsSetupSlideout {...props} />;
    case 'settings':
      return <GetemailsSlideoutEditorNewSettings {...props} />;
  }
};
